import type { StorageUnit } from 'store/storageunits/types';
import { StorageUnitType } from 'store/storageunits/types';
import StorageUnitsSection from './StorageUnitsSection';
import StorageUnitCard from './Cards/StorageUnitCard';
import Folder from './Cards/Folder';
import type { StackProps } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { WIDTH } from './Cards/Card';

type Props = StackProps & {
  title: string;
  units: StorageUnit[];
  type: StorageUnitType;
  showSorter?: boolean;
  noWrap?: boolean;
};

/**
 * Returns the number of unit cards to display based on
 * display size.
 * @returns how many cards to display
 */
export const useUnitDisplayLimit = () => {
  const MIN_LIMIT = 2;
  const two = WIDTH * MIN_LIMIT;
  const getLimit = (mul: number) => two + (WIDTH - 14) * mul;
  const matches = [
    useMediaQuery(`(min-width:${getLimit(3)}px)`),
    useMediaQuery(`(min-width:${getLimit(4)}px)`),
    useMediaQuery(`(min-width:${getLimit(5)}px)`),
    useMediaQuery(`(min-width:${getLimit(6)}px)`),
    useMediaQuery(`(min-width:${getLimit(7)}px)`),
    useMediaQuery(`(min-width:${getLimit(8)}px)`),
    useMediaQuery(`(min-width:${getLimit(9)}px)`),
    useMediaQuery(`(min-width:${getLimit(10)}px)`),
  ];

  const limit = matches.reduce(
    (acc, curr) => (curr ? acc + 1 : acc),
    MIN_LIMIT,
  );

  return limit;
};

const StorageUnitGrid = ({
  title,
  type,
  units,
  showSorter,
  noWrap,
  ...props
}: Props) => {
  const Comp = type === StorageUnitType.File ? StorageUnitCard : Folder;
  const limit = useUnitDisplayLimit();

  return (
    <StorageUnitsSection
      {...props}
      title={title}
      visible={units.length > 0}
      type={type}
      showSorter={showSorter}
    >
      {(noWrap ? units.slice(0, limit) : units).map((u) => (
        <Comp key={u.id} storageUnit={u} />
      ))}
    </StorageUnitsSection>
  );
};

export default StorageUnitGrid;
